/**
 * removeStuckVoyage_v3.js
 *
 * FT-484: Self-healing one-time IDB migration to remove a single stuck voyage
 * from a trial user's local IndexedDB.
 *
 * PROBLEM:
 *   A specific voyage (id: 4c57f186-3834-4616-975d-15ec18cef778) exists only in
 *   one trial user's local IDB — it does NOT exist on the server. The voyage is in
 *   a state that blocks the user from completing it (e.g. the completion flow
 *   requires a server record that will never arrive). Because the voyage lives
 *   purely client-side, a server-side fix is not possible; the record must be
 *   surgically removed from IDB on app boot.
 *
 * FIX:
 *   On startup, scan all operations_voyages records in IDB. For each record,
 *   filter record.value (the voyages array) to exclude the target voyage ID.
 *   If the voyage was found in any record, write the patched array back to IDB
 *   silently. Other voyages and all other fields are untouched.
 *
 * SCOPE:
 *   One voyage ID only. This migration is a targeted point-fix — it will silently
 *   no-op on every other device where that voyage does not exist.
 *
 * GUARD:
 *   localStorage flag sr.migration.removeStuckVoyage_v3 = 'complete' prevents
 *   re-runs. Written at the END, only on success. On error: flag is NOT set so
 *   the migration retries on next boot.
 *
 * PATTERN:
 *   Follows unlockAllStaleLocks_v2 (FT-482): localStorage flag guard, try/catch,
 *   reads/writes IDB (Dexie) storage table, console log on outcomes.
 *
 * CALLER:
 *   src/index.js — sequenced in the async IIFE AFTER v2, fire-and-forget after
 *   root.render() so React is already mounted before the write completes.
 */

import { db } from '../../lib/db';

const MIGRATION_FLAG = 'sr.migration.removeStuckVoyage_v3';
const TARGET_VOYAGE_ID = 'voyage-4c57f186-3834-4616-975d-15ec18cef778';

/**
 * Remove the stuck trial voyage from IDB if present.
 * @returns {Promise<boolean>} - Resolves true if migration ran, false if skipped.
 */
export const removeStuckVoyage_v3 = async () => {
  // Guard: already done on this device.
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-484] Starting stuck-voyage removal migration...');

    // Fetch all IDB records whose module is 'operations_voyages'.
    const voyageRecords = await db.storage
      .where('module')
      .equals('operations_voyages')
      .toArray();

    if (voyageRecords.length === 0) {
      console.log('[FT-484] No operations_voyages records in IDB — nothing to migrate.');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return true;
    }

    let recordsChecked = 0;
    let voyageFound = false;

    for (const record of voyageRecords) {
      recordsChecked++;

      const voyages = record.value;
      if (!Array.isArray(voyages)) continue;

      const hadTarget = voyages.some(v => v && v.id === TARGET_VOYAGE_ID);
      if (!hadTarget) continue;

      // Voyage found in this record — remove it.
      voyageFound = true;
      const patchedVoyages = voyages.filter(v => v && v.id !== TARGET_VOYAGE_ID);

      console.log(
        `[FT-484] Found stuck voyage ${TARGET_VOYAGE_ID} in IDB record (key=${record.key}). ` +
        `Removing (was ${voyages.length} voyage(s), now ${patchedVoyages.length}).`
      );

      await db.storage.put({
        ...record,
        value: patchedVoyages,
        timestamp: Date.now(),
      });
    }

    // Mark complete — only on success.
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (voyageFound) {
      console.log(
        `[FT-484] Migration complete: stuck voyage removed (${recordsChecked} record(s) checked).`
      );
    } else {
      console.log(
        `[FT-484] Migration complete: target voyage not present in ${recordsChecked} IDB record(s) — no-op.`
      );
    }

    return true;

  } catch (error) {
    console.error('[FT-484] Stuck-voyage removal migration failed:', error);
    // Do NOT set the flag — retry on next boot so the issue doesn't persist.
    return false;
  }
};
